class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.isOpen = false;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');

    this.setupEventListeners();

    this.quickSearchTrending.forEach(element => {
      element.addEventListener(
        'click',
        this.quickSearchTrendingClick.bind(this)
      );
    });
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');

    if (form) {
      form.addEventListener(
        'submit',
        this.onFormSubmit.bind(this)
      );
    }

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 500).bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );

    document.addEventListener(
      'click',
      this.onDocClick.bind(this)
    );

    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener(
        'click',
        this.onCloseStickySearchClick.bind(this)
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  /*
   * Toperkin precise search
   *
   * Normal keyword:
   * rabbit -> title:rabbit
   * bronze rabbit -> title:bronze title:rabbit
   *
   * SKU:
   * TPE-185B -> TPE-185B
   * TPAL-210 -> TPAL-210
   */
  buildSearchQuery(searchTerm) {
    const cleanTerm = searchTerm.trim();

    /*
     * Detect Toperkin-style SKU.
     * Must contain at least one number and
     * only letters, numbers, hyphens or underscores.
     */
    const isSku =
      /^[A-Za-z0-9_-]+$/.test(cleanTerm) &&
      /\d/.test(cleanTerm);

    /*
     * IMPORTANT:
     * Keep SKU unchanged so Shopify can use
     * its native SKU search.
     */
    if (isSku) {
      return cleanTerm;
    }

    /*
     * Normal customer keywords:
     * restrict search to product title.
     */
    const words = cleanTerm
      .split(/\s+/)
      .filter(word => word.length > 0);

    return words
      .map(word => `title:${word}`)
      .join(' ');
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    event.preventDefault();

    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    /*
     * If customer has actively selected a product
     * from predictive results, open that product.
     */
    const selectedProduct =
      this.querySelector('[aria-selected="true"] a');

    if (selectedProduct) {
      selectedProduct.click();
      return;
    }

    const query = this.buildSearchQuery(searchTerm);

    window.location.href =
      `${routes.search_url}` +
      `?q=${encodeURIComponent(query)}` +
      `&type=product` +
      `&options%5Bprefix%5D=none`;
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return this.showTrendingAndProducts();
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement =
      this.querySelector('[aria-selected="true"]');
    const allElements =
      this.querySelectorAll('li');

    let activeElement =
      this.querySelector('li');

    if (moveUp && !selectedElement) return;

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElement =
        selectedElement.nextElementSibling ||
        allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (!activeElement) return;
    if (activeElement === selectedElement) return;

    activeElement.setAttribute(
      'aria-selected',
      true
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        'aria-selected',
        false
      );
    }

    this.setLiveRegionText(
      activeElement.textContent
    );

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedProduct =
      this.querySelector(
        '[aria-selected="true"] a, [aria-selected="true"] button'
      );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const query = this.buildSearchQuery(searchTerm);

    const queryKey =
      query
        .replace(/\s+/g, '-')
        .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      this.updateViewAllLink(searchTerm);
      return;
    }

    const url =
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(query)}` +
      `&${encodeURIComponent('resources[type]')}=product` +
      `&${encodeURIComponent('resources[limit]')}=${this.productToShow}` +
      `&${encodeURIComponent('resources[options][prefix]')}=none` +
      `&section_id=predictive-search`;

    fetch(url)
      .then(response => {
        if (!response.ok) {
          const error =
            new Error(response.status);

          this.close();
          throw error;
        }

        return response.text();
      })
      .then(text => {
        const parsedDocument =
          new DOMParser().parseFromString(
            text,
            'text/html'
          );

        const section =
          parsedDocument.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup =
          section.innerHTML;

        this.cachedResults[queryKey] =
          resultsMarkup;

        this.renderSearchResults(
          resultsMarkup
        );

        this.updateViewAllLink(
          searchTerm
        );
      })
      .catch(error => {
        this.close();

        console.error(
          'Predictive search error:',
          error
        );
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText
      );
    }

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText;

    setTimeout(() => {
      this.statusElement.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      true
    );

    this.setAttribute(
      'open',
      true
    );

    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const count =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (count) {
      this.setLiveRegionText(
        count.textContent
      );
    }
  }

  getResultsMaxHeight() {
    const navigation =
      document.querySelector(
        '[class^="header-navigation"]'
      );

    if (!navigation) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      navigation
        .getBoundingClientRect()
        .bottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute(
      'open',
      true
    );

    this.input.setAttribute(
      'aria-expanded',
      true
    );

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute('open');

    this.input.setAttribute(
      'aria-expanded',
      false
    );

    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults
        .removeAttribute('style');
    }

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) {
      return;
    }

    this.trendingAndProductsBlock
      .classList.add('is-show');

    this.trendingAndProductsBlock
      .classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) {
      return;
    }

    this.trendingAndProductsBlock
      .classList.remove('is-show');

    this.trendingAndProductsBlock
      .classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal =
      this.contains(e.target);

    const target = e.target;

    if (
      !isInModal ||
      target.closest(
        '.header-search-popup-close'
      ) ||
      target.matches(
        '.header-search-popup-close'
      ) ||
      target.closest(
        '.header-search-close'
      ) ||
      target.matches(
        '.header-search-close'
      )
    ) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();

    document
      .querySelector('body')
      .classList.remove(
        'sticky-search-open'
      );

    document
      .querySelector('body')
      .classList.remove(
        'sticky-search-menu-open'
      );

    this
      .closest(
        '[class*="section-header-"]'
      )
      ?.classList.remove(
        'sticky-search-menu-open'
      );

    this
      .closest(
        '[class*="section-header-"]'
      )
      ?.classList.remove(
        'sticky-search-menu-custom-open'
      );
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink =
      this.querySelector(
        '[data-qs-view-all-link]'
      ) ||
      document.querySelector(
        '[data-qs-view-all-link]'
      );

    if (!qsViewAllLink) return;

    const query =
      this.buildSearchQuery(searchTerm);

    const linkTotal =
      `${routes.search_url}` +
      `?q=${encodeURIComponent(query)}` +
      `&type=product` +
      `&options%5Bprefix%5D=none`;

    qsViewAllLink.href =
      linkTotal;

    return this
      .getTotalResults(linkTotal)
      .then(count => {
        const original =
          qsViewAllLink.innerHTML;

        if (/\(\d*\)/.test(original)) {
          qsViewAllLink.innerHTML =
            original.replace(
              /\(\d*\)/,
              `(${count})`
            );
        }
      });
  }

  getTotalResults(url) {
    return fetch(url)
      .then(response => {
        if (!response.ok) {
          const error =
            new Error(response.status);

          this.close();
          throw error;
        }

        return response.text();
      })
      .then(text => {
        const element =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            )
            .querySelector(
              '[id^="SearchSection"]'
            );

        return element
          ? element.dataset.searchCount
          : 0;
      })
      .catch(error => {
        console.error(
          'Search count error:',
          error
        );

        return 0;
      });
  }

  quickSearchTrendingClick(event) {
    window.location =
      event.currentTarget
        .getAttribute('href');
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);